import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import GroupsIcon from '@mui/icons-material/Groups';
import { PARTNER_SWITCH_SX } from '../Partner/partnerDashboardTheme';
import RequireJoinApprovalField from '../shared/registration/RequireJoinApprovalField';
import AttendeeEligibilityField from '../shared/registration/AttendeeEligibilityField';
import { createCommunityCreateInitialState } from '../Partner/Community/communityEditConstants';

const CreateCommunityDialog = ({ open, partner, submitting = false, onClose, onSubmit }) => {
  const [form, setForm] = useState(createCommunityCreateInitialState);

  useEffect(() => {
    if (open) {
      setForm(createCommunityCreateInitialState());
    }
  }, [open, partner?.id]);

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit?.(form);
  };

  const updateField = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const partnerName = partner?.fullName?.trim() || 'this partner';

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Create Community</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2.5,
              color: 'primary.main',
            }}
          >
            <GroupsIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {partnerName}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
            Set community membership rules before creating the channel for this partner.
          </Typography>

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 0.5,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: '0.9375rem', color: 'text.primary', py: 1.5 }}
            >
              Community Settings
            </Typography>

            <RequireJoinApprovalField
              variant="community"
              checked={form.requireJoinApproval}
              onChange={updateField('requireJoinApproval')}
              SwitchComponent={Switch}
              switchSx={PARTNER_SWITCH_SX}
              bordered={false}
              disabled={submitting}
            />

            <AttendeeEligibilityField
              variant="community"
              value={form.attendeeEligibility}
              onChange={updateField('attendeeEligibility')}
              radioRow={false}
              showInfoAlert={false}
              bordered={false}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={submitting}
            disabled={submitting}
            sx={{ bgcolor: 'primary.main', textTransform: 'none', minWidth: 150 }}
          >
            Create Community
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateCommunityDialog;
